import Box from "@mui/material/Box";
import List from "@mui/material/List";
import { styled, SxProps, useTheme } from "@mui/material/styles";
import { Theme } from "@mui/material/styles/createTheme";
import FlexRowCenter from "designSystem/components/flex/FlexRowCenter";
import { ControllerProps } from "csi/middleware/common";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent } from "react";
import ModalDialog, { ModalDialogProps } from "../dialog/ModalDialog";
import { STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";
import StepItem from "./StepItem";
import WizardActions from "./WizardActions";
import WizardStore from "./WizardStore";

export class StepInfo {
	/** details information for this step */
	description?: string;

	/** subtitle for this step */
	subtitle?: string;

	/** title for this step */
	title: string;

	/** values extracted from the current step */
	values?: string[];

	constructor(title: string, subtitle: string, description?: string) {
		this.title = title;
		this.subtitle = subtitle;
		this.description = description;
		this.values = [];
	}
}

const PREFIX = "StepInfoBox";
const classes = {
	leftPane: `${PREFIX}leftPane`,
};

const StepInfoBox = styled(Box)(({ theme }) => ({
	[`& .${classes.leftPane}`]: {
		borderRight: STANDARD_BORDER_STYLE,
		maxWidth: 246,
		minWidth: 246,
		overflow: "auto",
		paddingTop: 10,
	},
}));

const generateBoxStyles = <T extends WizardStore>(props: WizardModalDialogProps<T>, theme: Theme): SxProps => {
	let { contentScrollable, contentPadding } = props;

	if (contentScrollable == null) {
		contentScrollable = true;
	}

	if (contentPadding == null) {
		contentPadding = theme.spacing(5);
	}

	return {
		flexGrow: 1,
		overflow: contentScrollable ? "auto" : "hidden",
		height: contentScrollable ? "auto" : "100%",
		padding: contentPadding,
	};
};

export interface WizardModalDialogProps<T extends WizardStore> extends ControllerProps, Omit<ModalDialogProps, "children"> {
	/** padding of the content */
	contentPadding?: number | string;

	/** indicate if the content can scroll */
	contentScrollable?: boolean;

	/** the instance of WizardStore */
	pages: FunctionComponent[];

	/** the instance of WizardStore */
	store: T;

	/** indicate if support the step information or not, if so, would show the left and right pane */
	supportStepInformation: boolean;
}

export const WizardDialog: FunctionComponent<WizardModalDialogProps<WizardStore>> = observer((props) => {
	const {
		pages,
		contentPadding,
		contentScrollable,
		store,
		supportStepInformation,
		...otherProps
	} = props;

	const theme = useTheme();
	const { cancel, currentStepInfo, index } = store;
	const { leftPane } = classes;
	const boxStyles = generateBoxStyles(props, theme);
	const CurrentPage = pages[index];
	const stepList = [];

	for (let i = 0; i < pages.length; ++i) {
		stepList.push(<StepItem
			key={`step${i}`}
			stepIndex={i}
			store={store}
		              />);
	}

	return (
		<ModalDialog
			contentSpacing={0}
			maxWidth="xl"
			onClose={cancel}
			scrollContent="belowMessage"
			showClose
			showDividers
			{...otherProps}
		>
			{{
				buttons: (
					<FlexRowCenter flexGrow="1" justifyContent="flex-end">
						<WizardActions store={store} />
					</FlexRowCenter>
				),
				main: supportStepInformation ? (
					<StepInfoBox
						display="flex"
						height={1}
						width={1}
					>
						<Box className={leftPane}>
							<List>
								{stepList}
							</List>
						</Box>
						<Box sx={boxStyles}>
							{currentStepInfo?.description && (
								<Box mb={5}>
									{currentStepInfo.description}
								</Box>
							)}
							<CurrentPage />
						</Box>
					</StepInfoBox>
				)
					:
					<CurrentPage />,
			}}
		</ModalDialog>
	);
});

export default WizardDialog;